import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    FaShoppingCart, FaHeart, FaSearch, FaUserCircle,
    FaCoins, FaTimes, FaMapMarkerAlt, FaCheckCircle, FaPhone
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

/* ─────────────────────────────────────────────
   Fresh Food Color Palette
   Russian Red:     #CD0302
   Sunburst:        #F0C400
   Edam:            #F5D19D
   Citrusy Orange:  #F88702
   Autumn Green:    #4F7E17

   bg:       #fff
   primary:  #4F7E17  (Autumn Green — main interactive/brand)
   accent:   #CD0302  (Russian Red — alerts, badges, danger)
   highlight:#F88702  (Citrusy Orange — secondary accent)
   warm bg:  #FFF8EC  (derived from Edam — light surface)
   border:   #F5D19D  (Edam — borders)
   text:     #615e58
   font:     Segoe UI, Helvetica, Droid Sans, Arial, lucida grande, tahoma, verdana
───────────────────────────────────────────── */

const CHALDAL_FONT = "'Segoe UI', Helvetica, 'Droid Sans', Arial, 'lucida grande', tahoma, verdana, arial, sans-serif";

function Header({ cartItemCount = 0, onSearchClick }) {
    const navigate = useNavigate();
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showNewUserBadge, setShowNewUserBadge] = useState(false);
    const [showLoginToast, setShowLoginToast] = useState(false);

    // Realtime Search
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchTimeoutRef = useRef(null);

    const [isMobile, setIsMobile] = useState(false);
    const [placeholderIndex, setPlaceholderIndex] = useState(0);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    const [headerData, setHeaderData] = useState({
        logoUrl: '',
        companyName: 'Aman LifeStyle',
        phone: '01767298388',
        keywords: ['Search for products', 'Keyboard', 'Headphones', 'Laptop', 'Electronics', 'Fashion', 'Mobile Phones', 'Watches']
    });

    const { isAuthenticated, userInfo, logout } = useAuth();

    useEffect(() => {
        const justLoggedIn = localStorage.getItem('just_logged_in');
        if (justLoggedIn === 'true' && isAuthenticated) {
            setShowLoginToast(true);
            setShowNewUserBadge(true);
            localStorage.removeItem('just_logged_in');
            setTimeout(() => setShowLoginToast(false), 4000);
            setTimeout(() => setShowNewUserBadge(false), 10000);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        const fetchHeaderData = async () => {
            try {
                const response = await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/api/header');
                const result = await response.json();
                if (result.success && result.header) {
                    setHeaderData({
                        logoUrl: result.header.logoUrl || '',
                        companyName: result.header.companyName || 'Aman LifeStyle',
                        phone: result.header.phone || '01767298388',
                        keywords: (result.header.keywords?.length > 0) ? result.header.keywords : ['Search for products', 'Keyboard', 'Headphones', 'Laptop'],
                    });
                }
            } catch (e) { console.error('Error fetching header data:', e); }
        };
        fetchHeaderData();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setPlaceholderIndex(p => (p + 1) % headerData.keywords.length);
        }, 2000);
        return () => clearInterval(interval);
    }, [headerData.keywords]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isUserDropdownOpen && !e.target.closest('.user-dropdown-container')) setIsUserDropdownOpen(false);
            if (isMobileMenuOpen && !e.target.closest('.mobile-menu') && !e.target.closest('.mobile-menu-button')) setIsMobileMenuOpen(false);
            if (isMobileSearchOpen && !e.target.closest('.mobile-search-sidebar') && !e.target.closest('.mobile-search-button')) setIsMobileSearchOpen(false);
        };
        const handleScroll = () => { if (isMobileMenuOpen) setIsMobileMenuOpen(false); };
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll);
        return () => { document.removeEventListener('mousedown', handleClickOutside); window.removeEventListener('scroll', handleScroll); };
    }, [isUserDropdownOpen, isMobileMenuOpen, isMobileSearchOpen]);

    // Search handlers
    const handleSearchInput = async (value) => {
        setSearchQuery(value);
        if (value.trim().length < 2) { setSearchResults([]); setShowDropdown(false); clearTimeout(searchTimeoutRef.current); return; }
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                const res = await fetch(`https://bazar-wala-main-server-974485829900.asia-south1.run.app/api/search/products?q=${encodeURIComponent(value.trim())}&limit=8`);
                const data = await res.json();
                if (data.success) { setSearchResults(data.products); setShowDropdown(true); }
            } catch (e) { console.error('Search error:', e); }
            finally { setIsSearching(false); }
        }, 300);
    };

    const slugify = (text) => text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').trim();

    const handleProductClick = (product) => {
        setShowDropdown(false); setSearchQuery(''); setSearchResults([]); setIsMobileSearchOpen(false);
        navigate(`/product/${product.productId}/${slugify(product.name)}`);
    };

    const handleUserIconClick = () => {
        isAuthenticated ? setIsUserDropdownOpen(p => !p) : navigate('/login');
    };
    const handleLogoutClick = () => { logout(); setIsUserDropdownOpen(false); navigate('/'); };
    const handleNavLinkClick = () => { setIsMobileMenuOpen(false); setIsUserDropdownOpen(false); };
    const getUserPoints = () => (userInfo && typeof userInfo.points === 'number' ? userInfo.points : 0);

    const handleMobileSearchToggle = () => {
        setIsMobileSearchOpen(p => !p);
        if (isMobileSearchOpen) { setSearchQuery(''); setSearchResults([]); setShowDropdown(false); }
    };

    return (
        <>
            {/* Spacer */}
            <div style={{ height: isMobile ? '60px' : '100px' }} />

            {/* ── Login Toast ── */}
            {showLoginToast && (
                <div style={{ position: 'fixed', top: '80px', right: '16px', zIndex: 9999, animation: 'slideInRight 0.3s ease-out' }}>
                    <div style={{ background: '#fff', border: '2px solid #4F7E17', borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', minWidth: '260px', boxShadow: '0 4px 20px rgba(79,126,23,0.15)' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#4F7E17', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <FaCheckCircle style={{ color: '#fff', fontSize: '18px' }} />
                        </div>
                        <div>
                            <p style={{ fontSize: '13px', fontWeight: 700, color: '#333', margin: 0 }}>Welcome!</p>
                            <p style={{ fontSize: '12px', color: '#888', margin: 0 }}>You're now logged in</p>
                        </div>
                        <button onClick={() => setShowLoginToast(false)} style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', marginLeft: 'auto' }}>
                            <FaTimes />
                        </button>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════
                FIXED HEADER
            ══════════════════════════════════ */}
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, fontFamily: CHALDAL_FONT, fontSize: '12px', color: '#615e58' }}>

                {/* ── Main Header Bar ── */}
                <div style={{ background: '#fff', borderBottom: '1px solid #F5D19D', boxShadow: '0 1px 4px rgba(79,126,23,0.08)' }}>
                    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 16px', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%', boxSizing: 'border-box', height: '60px', gap: '12px' }}>

                        {/* LEFT */}
                        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>

                            {/* Mobile ONLY: 3-bar / profile icon */}
                            {isMobile && (
                                <button
                                    onClick={() => setIsMobileMenuOpen(p => !p)}
                                    className="mobile-menu-button"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', position: 'relative' }}
                                >
                                    {isAuthenticated && userInfo?.picture ? (
                                        <div style={{ position: 'relative' }}>
                                            <img src={userInfo.picture} alt="User" style={{ width: '32px', height: '32px', borderRadius: '50%', border: '2px solid #4F7E17', objectFit: 'cover' }} />
                                            {showNewUserBadge && <span style={{ position: 'absolute', bottom: 0, right: 0, width: '9px', height: '9px', background: '#4F7E17', borderRadius: '50%', border: '2px solid #fff' }} />}
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '2px' }}>
                                            <span style={{ display: 'block', width: '18px', height: '2px', background: '#615e58', borderRadius: '2px' }} />
                                            <span style={{ display: 'block', width: '18px', height: '2px', background: '#615e58', borderRadius: '2px' }} />
                                            <span style={{ display: 'block', width: '18px', height: '2px', background: '#615e58', borderRadius: '2px' }} />
                                        </div>
                                    )}
                                </button>
                            )}

                            {/* Desktop ONLY: Logo */}
                            {!isMobile && (
                                <Link to="/" onClick={handleNavLinkClick} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                    {headerData.logoUrl
                                        ? <img src={headerData.logoUrl} alt="Logo" style={{ height: '138px', width: '238px', objectFit: 'contain' }} />
                                        : <div style={{ fontSize: '22px', fontWeight: 900, color: '#4F7E17', letterSpacing: '-0.03em' }}>AL</div>
                                    }
                                </Link>
                            )}
                        </div>

                        {/* Mobile: centered logo */}
                        {isMobile && (
                            <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                                <Link to="/" onClick={handleNavLinkClick} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                    {headerData.logoUrl
                                        ? <img src={headerData.logoUrl} alt="Logo" style={{ height: '36px', width: '236px', objectFit: 'contain' }} />
                                        : <div style={{ fontSize: '20px', fontWeight: 900, color: '#4F7E17' }}>AL</div>
                                    }
                                </Link>
                            </div>
                        )}

                        {/* Desktop: Search bar */}
                        {!isMobile && (
                            <div style={{ flex: 1, position: 'relative', margin: '0 16px' }}>
                                <div style={{ display: 'flex', width: '100%', border: '2px solid #4F7E17', borderRadius: '6px', overflow: 'hidden' }}>
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={e => handleSearchInput(e.target.value)}
                                        onFocus={() => { if (searchResults.length > 0) setShowDropdown(true); }}
                                        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                        placeholder={headerData.keywords[placeholderIndex]}
                                        style={{
                                            flex: 1, padding: '9px 14px', border: 'none', outline: 'none',
                                            fontSize: '13px', color: '#333', background: '#fff',
                                            fontFamily: CHALDAL_FONT,
                                        }}
                                    />
                                    <button style={{ padding: '0 18px', background: '#4F7E17', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        {isSearching
                                            ? <div style={{ width: '16px', height: '16px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                                            : <svg style={{ width: '18px', height: '18px', fill: '#fff' }} viewBox="0 0 100 100">
                                                <path d="M44.5,78.5c-18.8,0-34-15.3-34-34s15.3-34,34-34s34,15.3,34,34S63.3,78.5,44.5,78.5z M44.5,18.1C30,18.1,18.2,30,18.2,44.5S30,70.8,44.5,70.8S70.9,59,70.9,44.5S59,18.1,44.5,18.1z" />
                                                <path d="M87.2,91c-1,0-2-0.4-2.7-1.1L63.1,68.5c-1.5-1.5-1.5-3.9,0-5.4s3.9-1.5,5.4,0l21.3,21.3c1.5,1.5,1.5,3.9,0,5.4C89.2,90.6,88.2,91,87.2,91z" />
                                            </svg>
                                        }
                                    </button>
                                </div>
                                {showDropdown && searchResults.length > 0 && (
                                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #F5D19D', borderRadius: '0 0 8px 8px', boxShadow: '0 8px 24px rgba(79,126,23,0.12)', zIndex: 100, maxHeight: '380px', overflowY: 'auto' }}>
                                        {searchResults.map(product => (
                                            <div key={product.productId} onMouseDown={() => handleProductClick(product)}
                                                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #FFF8EC', transition: 'background 0.1s' }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#FFF8EC'}
                                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                            >
                                                {product.image && <img src={product.image} alt={product.name} style={{ width: '38px', height: '38px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #F5D19D', flexShrink: 0 }} />}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: '13px', color: '#333', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</div>
                                                    <div style={{ fontSize: '12px', color: '#4F7E17', fontWeight: 700 }}>৳{product.price}</div>
                                                </div>
                                                <span style={{ fontSize: '11px', color: product.stock > 0 ? '#4F7E17' : '#CD0302', flexShrink: 0 }}>
                                                    {product.stock > 0 ? 'In Stock' : 'Out'}
                                                </span>
                                            </div>
                                        ))}
                                        <div style={{ padding: '8px 14px', background: '#FFF8EC', textAlign: 'center', fontSize: '11px', color: '#888' }}>{searchResults.length} results</div>
                                    </div>
                                )}
                                {showDropdown && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #F5D19D', borderRadius: '0 0 8px 8px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 100, padding: '20px', textAlign: 'center', fontSize: '13px', color: '#888' }}>
                                        কোনো পণ্য পাওয়া যায়নি
                                    </div>
                                )}
                            </div>
                        )}

                        {/* RIGHT: Icons */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0, marginLeft: 'auto' }}>

                            {/* Track Parcel — mobile only */}
                            {isMobile && (
                                <Link to="/track-parcel/" onClick={handleNavLinkClick} style={{ padding: '6px 8px', color: '#615e58', display: 'flex', alignItems: 'center' }}>
                                    <FaMapMarkerAlt style={{ fontSize: '18px', color: '#F88702' }} />
                                </Link>
                            )}

                            {/* Search icon — mobile only */}
                            {isMobile && (
                                <button onClick={handleMobileSearchToggle} className="mobile-search-button" style={{ padding: '6px 8px', background: 'none', border: 'none', cursor: 'pointer', color: '#615e58', display: 'flex', alignItems: 'center' }}>
                                    <FaSearch style={{ fontSize: '18px', color: '#615e58' }} />
                                </button>
                            )}

                            {/* Phone — desktop only */}
                            {!isMobile && (
                                <a href={`tel:${headerData.phone}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', textDecoration: 'none', color: '#615e58', borderRadius: '6px', transition: 'background 0.15s' }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#FFF8EC'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px', fill: '#25D366', flexShrink: 0 }}>
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                    </svg>
                                    <div>
                                        <div style={{ fontSize: '10px', color: '#aaa', lineHeight: 1.2 }}>Call us</div>
                                        <div style={{ fontSize: '12px', color: '#333', fontWeight: 700, lineHeight: 1.2 }}>{headerData.phone}</div>
                                    </div>
                                </a>
                            )}

                            {/* User — desktop only */}
                            {!isMobile && (
                                <div className="user-dropdown-container" style={{ position: 'relative' }}>
                                    <button
                                        onClick={handleUserIconClick}
                                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '6px 8px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '6px', transition: 'background 0.15s', color: '#615e58', gap: '2px' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#FFF8EC'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <div style={{ position: 'relative' }}>
                                            {isAuthenticated && userInfo?.picture
                                                ? <img src={userInfo.picture} alt="User" style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2px solid #4F7E17', objectFit: 'cover' }} />
                                                : <FaUserCircle style={{ fontSize: '24px', color: '#615e58' }} />
                                            }
                                            {showNewUserBadge && (
                                                <span style={{ position: 'absolute', top: '-2px', right: '-2px', width: '10px', height: '10px', background: '#F88702', borderRadius: '50%', border: '2px solid #fff' }} />
                                            )}
                                        </div>
                                        {isAuthenticated && userInfo?.name && (
                                            <span style={{ fontSize: '10px', color: '#615e58', maxWidth: '52px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {userInfo.name.split(' ')[0]}
                                            </span>
                                        )}
                                    </button>

                                    {isUserDropdownOpen && (
                                        <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', width: '240px', background: '#fff', borderRadius: '8px', border: '1px solid #F5D19D', boxShadow: '0 8px 24px rgba(79,126,23,0.12)', zIndex: 200, overflow: 'hidden' }}>
                                            <div style={{ background: 'linear-gradient(135deg, #4F7E17, #7ab52e)', padding: '14px 16px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {userInfo?.picture
                                                            ? <img src={userInfo.picture} alt="User" style={{ width: '36px', height: '36px', borderRadius: '50%', border: '2px solid #fff', objectFit: 'cover' }} />
                                                            : <FaUserCircle style={{ fontSize: '28px', color: '#fff' }} />
                                                        }
                                                        <span style={{ color: '#fff', fontWeight: 700, fontSize: '13px' }}>{userInfo?.name}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(255,255,255,0.2)', padding: '3px 8px', borderRadius: '12px' }}>
                                                        <FaCoins style={{ color: '#F0C400', fontSize: '12px' }} />
                                                        <span style={{ color: '#fff', fontSize: '12px', fontWeight: 700 }}>{getUserPoints()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ padding: '6px 0' }}>
                                                {[
                                                    { to: '/profile', label: 'Your Profile' },
                                                    { to: '/order-history', label: 'Your Orders' },
                                                    { to: '/favorites', label: 'Your Wish List' },
                                                ].map(item => (
                                                    <Link key={item.to} to={item.to} onClick={handleNavLinkClick} style={{ display: 'block', padding: '9px 16px', fontSize: '13px', color: '#333', textDecoration: 'none', transition: 'background 0.1s' }}
                                                        onMouseEnter={e => e.currentTarget.style.background = '#FFF8EC'}
                                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                    >
                                                        {item.label}
                                                    </Link>
                                                ))}
                                                <div style={{ borderTop: '1px solid #F5D19D', marginTop: '4px' }}>
                                                    <button onClick={handleLogoutClick} style={{ width: '100%', padding: '9px 16px', fontSize: '13px', color: '#CD0302', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s' }}
                                                        onMouseEnter={e => e.currentTarget.style.background = '#fff5f5'}
                                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                    >
                                                        Sign Out
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Cart — desktop only */}
                            {!isMobile && (
                                <Link to="/cart" onClick={handleNavLinkClick} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', color: '#615e58', textDecoration: 'none', borderRadius: '6px', transition: 'background 0.15s', position: 'relative' }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#FFF8EC'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <div style={{ position: 'relative' }}>
                                        <FaShoppingCart style={{ fontSize: '22px', color: '#4F7E17' }} />
                                        {cartItemCount > 0 && (
                                            <span style={{ position: 'absolute', top: '-6px', left: '14px', background: '#CD0302', color: '#fff', fontSize: '10px', fontWeight: 800, borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>
                                                {cartItemCount}
                                            </span>
                                        )}
                                    </div>
                                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#333' }}>Cart</span>
                                </Link>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Desktop Nav Bar ── */}
                {!isMobile && (
                    <div style={{ background: '#FFF8EC', borderBottom: '1px solid #F5D19D' }}>
                        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 16px', display: 'flex', alignItems: 'center', gap: '2px', height: '40px' }}>
                            {[
                                { to: '/product', label: 'Products' },
                                { to: '/order-history', label: 'Orders', authOnly: true },
                                { to: '/myorders', label: 'My Orders' },
                                { to: '/track-parcel/', label: 'Track Parcel' },
                                { to: '/about', label: 'About' },
                                { to: '/contact-us', label: 'Contact' },
                                { to: '/refund-policy', label: 'Refund' },
                                { to: '/faq', label: 'FAQ' },
                            ].filter(item => !item.authOnly || isAuthenticated).map(item => (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    onClick={handleNavLinkClick}
                                    style={{ padding: '6px 12px', fontSize: '12px', color: '#615e58', textDecoration: 'none', borderRadius: '4px', fontWeight: 500, transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#F5D19D'; e.currentTarget.style.color = '#4F7E17'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#615e58'; }}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ══════════════════════════════════
                MOBILE SEARCH SIDEBAR (right)
            ══════════════════════════════════ */}
            <div
                className="mobile-search-sidebar"
                style={{ position: 'fixed', right: 0, top: 0, height: '100%', width: '320px', background: '#fff', boxShadow: '-4px 0 20px rgba(79,126,23,0.12)', zIndex: 60, overflowY: 'auto', transform: isMobileSearchOpen ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.3s ease', fontFamily: CHALDAL_FONT }}
            >
                <div style={{ background: '#4F7E17', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Search Products</span>
                    <button onClick={handleMobileSearchToggle} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '18px' }}><FaTimes /></button>
                </div>
                <div style={{ padding: '16px' }}>
                    <div style={{ border: '2px solid #4F7E17', borderRadius: '6px', overflow: 'hidden', display: 'flex', marginBottom: '12px' }}>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => handleSearchInput(e.target.value)}
                            placeholder={headerData.keywords[placeholderIndex]}
                            style={{ flex: 1, padding: '10px 12px', border: 'none', outline: 'none', fontSize: '13px', fontFamily: CHALDAL_FONT }}
                        />
                        {isSearching && <div style={{ padding: '0 12px', display: 'flex', alignItems: 'center' }}><div style={{ width: '14px', height: '14px', border: '2px solid #4F7E17', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /></div>}
                    </div>

                    {searchResults.length > 0 && (
                        <div style={{ border: '1px solid #F5D19D', borderRadius: '8px', overflow: 'hidden' }}>
                            {searchResults.map(product => (
                                <div key={product.productId} onClick={() => handleProductClick(product)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #FFF8EC' }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#FFF8EC'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                >
                                    {product.image && <img src={product.image} alt={product.name} style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #F5D19D' }} />}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '13px', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</div>
                                        <div style={{ fontSize: '12px', color: '#4F7E17', fontWeight: 700 }}>৳{product.price}</div>
                                    </div>
                                    <span style={{ fontSize: '10px', color: product.stock > 0 ? '#4F7E17' : '#CD0302' }}>{product.stock > 0 ? '✓' : '✗'}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontSize: '13px', border: '1px solid #F5D19D', borderRadius: '8px' }}>কোনো পণ্য পাওয়া যায়নি</div>
                    )}

                    {searchQuery.length === 0 && (
                        <div style={{ marginTop: '16px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#333', marginBottom: '10px' }}>Popular Searches</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {headerData.keywords.slice(1).map((kw, i) => (
                                    <button key={i} onClick={() => handleSearchInput(kw)}
                                        style={{ padding: '5px 10px', background: '#FFF8EC', border: '1px solid #F5D19D', borderRadius: '20px', fontSize: '12px', color: '#615e58', cursor: 'pointer', transition: 'all 0.15s' }}
                                        onMouseEnter={e => { e.currentTarget.style.background = '#F5D19D'; e.currentTarget.style.color = '#4F7E17'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = '#FFF8EC'; e.currentTarget.style.color = '#615e58'; }}
                                    >
                                        {kw}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ══════════════════════════════════
                MOBILE MENU SIDEBAR (left)
            ══════════════════════════════════ */}
            <div
                className="mobile-menu"
                style={{
                    position: 'fixed', left: 0, top: 0, height: '100%', width: '290px',
                    background: '#fff', zIndex: 60, overflowY: 'auto',
                    transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
                    transition: 'transform 0.32s cubic-bezier(0.4,0,0.2,1)',
                    fontFamily: "'Nunito', 'Segoe UI', Helvetica, sans-serif",
                    boxShadow: isMobileMenuOpen ? '6px 0 32px rgba(79,126,23,0.18)' : 'none',
                }}
            >
                {/* Header with user info */}
                <div style={{ background: 'linear-gradient(135deg, #4F7E17 0%, #7ab52e 100%)', padding: '28px 20px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ position: 'relative' }}>
                                {isAuthenticated && userInfo?.picture
                                    ? <img src={userInfo.picture} alt="User" style={{ width: '48px', height: '48px', borderRadius: '50%', border: '3px solid rgba(255,255,255,0.6)', objectFit: 'cover' }} />
                                    : <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <FaUserCircle style={{ fontSize: '28px', color: '#fff' }} />
                                    </div>
                                }
                                {showNewUserBadge && <span style={{ position: 'absolute', bottom: '1px', right: '1px', width: '12px', height: '12px', background: '#F0C400', borderRadius: '50%', border: '2px solid #fff' }} />}
                            </div>
                            <div>
                                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11px', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '3px' }}>
                                    {isAuthenticated ? 'Welcome back' : 'Guest'}
                                </div>
                                <div style={{ color: '#fff', fontWeight: 800, fontSize: '16px', letterSpacing: '-0.01em' }}>
                                    {isAuthenticated && userInfo?.name ? userInfo.name.split(' ')[0] : 'Sign in'}
                                </div>
                                {isAuthenticated && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                                        <FaCoins style={{ color: '#F0C400', fontSize: '11px' }} />
                                        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '11px', fontWeight: 600 }}>{getUserPoints()} points</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <button onClick={() => setIsMobileMenuOpen(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {/* Nav Links */}
                <div style={{ padding: '12px 0 100px' }}>

                    {/* Cart */}
                    <Link to="/cart" onClick={handleNavLinkClick}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 20px', textDecoration: 'none', color: '#333', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#FFF8EC'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{ width: '34px', height: '34px', background: '#edf5e0', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FaShoppingCart style={{ color: '#4F7E17', fontSize: '15px' }} />
                            </div>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: '#222' }}>My Cart</span>
                        </div>
                        {cartItemCount > 0 && (
                            <span style={{ background: '#CD0302', color: '#fff', borderRadius: '20px', minWidth: '22px', height: '22px', fontSize: '11px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px' }}>{cartItemCount}</span>
                        )}
                    </Link>

                    <Link to="/favorites" onClick={handleNavLinkClick}
                        style={{ display: 'flex', alignItems: 'center', padding: '13px 20px', textDecoration: 'none', color: '#333', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#FFF8EC'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                        <div style={{ width: '34px', height: '34px', background: '#fff0f0', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: '14px' }}>
                            <FaHeart style={{ color: '#CD0302', fontSize: '15px' }} />
                        </div>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#222' }}>Wish List</span>
                    </Link>

                    {/* Divider */}
                    <div style={{ margin: '8px 20px', borderTop: '1px dashed #F5D19D' }} />

                    {/* Section label */}
                    <div style={{ padding: '8px 20px 4px', fontSize: '10px', fontWeight: 800, color: '#b0a8c8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Shop</div>

                    {[
                        { to: '/product', label: 'All Products', emoji: '🛍️' },
                        { to: '/myorders', label: 'My Orders', emoji: '📦' },
                        { to: '/track-parcel/', label: 'Track Parcel', emoji: '🚚' },
                    ].map(item => (
                        <Link key={item.to} to={item.to} onClick={handleNavLinkClick}
                            style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 20px', fontSize: '14px', fontWeight: 600, color: '#222', textDecoration: 'none', transition: 'background 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#FFF8EC'; e.currentTarget.style.color = '#4F7E17'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#222'; }}
                        >
                            <span style={{ fontSize: '18px', width: '34px', textAlign: 'center' }}>{item.emoji}</span>
                            {item.label}
                        </Link>
                    ))}

                    <div style={{ margin: '8px 20px', borderTop: '1px dashed #F5D19D' }} />
                    <div style={{ padding: '8px 20px 4px', fontSize: '10px', fontWeight: 800, color: '#b0a8c8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Help & Info</div>

                    {[
                        ...(isAuthenticated ? [
                            { to: '/profile', label: 'Your Profile', emoji: '👤' },
                            { to: '/order-history', label: 'Order History', emoji: '🧾' },
                        ] : []),
                        { to: '/about', label: 'About Us', emoji: 'ℹ️' },
                        { to: '/contact-us', label: 'Contact Us', emoji: '📞' },
                        { to: '/refund-policy', label: 'Refund Policy', emoji: '↩️' },
                        { to: '/faq', label: 'FAQ', emoji: '❓' },
                    ].map(item => (
                        <Link key={item.to} to={item.to} onClick={handleNavLinkClick}
                            style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '11px 20px', fontSize: '13px', fontWeight: 500, color: '#555', textDecoration: 'none', transition: 'background 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#FFF8EC'; e.currentTarget.style.color = '#4F7E17'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#555'; }}
                        >
                            <span style={{ fontSize: '16px', width: '34px', textAlign: 'center' }}>{item.emoji}</span>
                            {item.label}
                        </Link>
                    ))}

                    {/* Sign in / Sign out */}
                    <div style={{ padding: '16px 20px 4px' }}>
                        {isAuthenticated ? (
                            <button onClick={handleLogoutClick} style={{ width: '100%', padding: '12px', background: 'transparent', border: '2px solid #CD0302', borderRadius: '10px', color: '#CD0302', fontWeight: 700, fontSize: '14px', cursor: 'pointer', fontFamily: "'Nunito', 'Segoe UI', sans-serif", letterSpacing: '0.02em', transition: 'all 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#CD0302'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#CD0302'; }}
                            >
                                Sign Out
                            </button>
                        ) : (
                            <button onClick={() => { navigate('/login'); handleNavLinkClick(); }} style={{ width: '100%', padding: '12px', background: '#4F7E17', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 700, fontSize: '14px', cursor: 'pointer', fontFamily: "'Nunito', 'Segoe UI', sans-serif", letterSpacing: '0.02em', boxShadow: '0 4px 12px rgba(79,126,23,0.3)' }}>
                                Sign In
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Overlays */}
            {(isMobileMenuOpen || isMobileSearchOpen) && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 55 }}
                    onClick={() => { setIsMobileMenuOpen(false); setIsMobileSearchOpen(false); }}
                />
            )}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800;900&display=swap');
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
            `}</style>
        </>
    );
}

export default Header;